import { getDomainElementsWithSelectabilityApi } from '@core/core/api'
import {
  checkCreatedTreeObject,
  deletePolygonFromProject,
  publishElementsByEntityId,
  selectActiveEntity,
  selectActiveProject,
  selectActiveTab,
  selectActiveWorkAreaId,
  selectTabDomainType,
  setActiveEntity,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import { useCubeLine, useTabsContext, useUpdateTabDomain } from '@core/hooks'
import type { ChannelMessage, MessageHandler } from 'common/messenger'
import { messengerRef } from 'common/messenger'
import type { FC } from 'react'
import { lazy, useEffect } from 'react'

import { getActiveEntity } from '../../helpers'
import type { MicrofrontendProps, SelectedTreeObject } from '../../types'
import { TabWrapper } from '../TabWrapper'

const Window3DFront = lazy(() => import('@window3d/Window3D'))

export const Window3DMF: FC<MicrofrontendProps> = props => {
  const dispatch = useAppDispatch()
  const entities = useTabsContext(props.tabId)
  const selectedTreeObject = useAppSelector(selectActiveEntity)
  const activeEntity = getActiveEntity(selectedTreeObject)
  const activeProject = useAppSelector(selectActiveProject)
  const activeWorkAreaId = useAppSelector(selectActiveWorkAreaId)
  const domainType = useAppSelector(state => selectTabDomainType(state, props.tabId))
  const projectId = activeProject.id
  const activeTabId = useAppSelector(selectActiveTab)?.id

  const isCurrentlyActive = activeTabId === props.tabId

  const { updateActiveTabDomain } = useUpdateTabDomain(projectId, activeWorkAreaId, props.tabId)

  const getSelectableElementsForDomain = () => {
    if (activeTabId) {
      void getDomainElementsWithSelectabilityApi(projectId, activeTabId)
    }
  }

  useCubeLine(entities, props.tabId, projectId, isCurrentlyActive)

  useEffect(() => {
    if (isCurrentlyActive) {
      getSelectableElementsForDomain()
      const unsubscribe = messengerRef.current?.subscribe('updateTabDomain', updateActiveTabDomain)

      return unsubscribe
    }
  }, [isCurrentlyActive])

  useEffect(() => {
    if (domainType === 'ANY') {
      getSelectableElementsForDomain()
    }
  }, [domainType])

  useEffect(() => {
    const onTreeNodeSelected: MessageHandler<'elementSelected'> = ({ payload }) => {
      const { entityId, entityType, entityName } = payload
      const treeObject: SelectedTreeObject = {
        entityId,
        entityType,
        entityName,
      }
      if (entityId) {
        dispatch(checkCreatedTreeObject(projectId, entityId, entityType))
      }

      dispatch(setActiveEntity(treeObject))
    }
    const unsubscribe = messengerRef.current?.subscribe('elementSelected', onTreeNodeSelected)

    return unsubscribe
  }, [])

  useEffect(() => {
    const onDeletePolygon: MessageHandler<'deletePolygonFromProject'> = ({
      payload: polygonId,
    }) => {
      dispatch(deletePolygonFromProject({ projectId, polygonId }))
    }

    const unsubscribe = messengerRef.current?.subscribe('deletePolygonFromProject', onDeletePolygon)
    return unsubscribe
  }, [])

  useEffect(() => {
    function publishElementHandler({ payload: entityId }: ChannelMessage<'publishElement'>) {
      const entity = entities.find(entity => entity.entityId === entityId)
      if (entity) {
        dispatch(publishElementsByEntityId(projectId, entity))
      }
    }

    const unsubscribe = messengerRef.current?.subscribe('publishElement', publishElementHandler)

    return unsubscribe
  }, [entities, projectId])

  const allProps = {
    ...props,
    entities,
    activeEntity,
    projectId,
    domainType,
    coreMessenger: messengerRef.current ?? undefined,
  }

  return (
    <TabWrapper>
      <Window3DFront {...allProps} />
    </TabWrapper>
  )
}
